import LegalLayout, { Section } from "@/components/legal/LegalLayout";

export default function DataSecurity() {
  return (
    <LegalLayout title="Data Security" updated="July 2, 2026" testid="data-security-page">
      <p>
        Security is the foundation of BLOQ Talk. This page explains the technical and
        organizational measures we use to protect your data across the BLOQ Talk mobile apps,
        the web application at bloqtalk.app, and related services (the "Service"). It supplements
        our{" "}
        <a href="/privacy" className="text-bloq-mint hover:underline">Privacy Policy</a> and{" "}
        <a href="/terms" className="text-bloq-mint hover:underline">Terms of Service</a>.
      </p>

      <Section heading="1. End-to-End Encryption">
        <p>
          Direct conversations on BLOQ Talk are end-to-end encrypted (E2EE). Message content is
          encrypted on your device and can only be read by you and your intended recipient. Our
          servers relay and store only ciphertext for E2EE conversations — we cannot decrypt or
          read the contents, and we never sell your messages or content.
        </p>
      </Section>

      <Section heading="2. Post-Quantum Cryptography">
        <p>
          BLOQ Talk is engineered to resist "harvest-now, decrypt-later" attacks. We layer
          post-quantum key exchange with established elliptic-curve cryptography so that keys and
          messages remain protected even against future quantum adversaries. Every message is
          cryptographically signed to guarantee authenticity and integrity.
        </p>
      </Section>

      <Section heading="3. Encryption in Transit and at Rest">
        <p>
          All connections between your device and our infrastructure are protected with strong
          TLS (HTTPS). Data stored on our systems is encrypted at rest. Sensitive fields and
          message payloads are protected with additional application-layer encryption.
        </p>
      </Section>

      <Section heading="4. Credential Protection">
        <ul className="list-disc space-y-2 pl-6">
          <li>Passwords are stored only as salted, one-way hashes — never in plain text.</li>
          <li>
            Administrator access requires multi-factor authentication (MFA/TOTP), and sensitive
            actions require step-up re-authentication.
          </li>
          <li>
            We monitor for credential stuffing and brute-force attempts and apply automatic
            lockouts to protect accounts.
          </li>
        </ul>
      </Section>

      <Section heading="5. Wallet Safety">
        <p>
          BLOQ Talk is a messenger, not a wallet. Optional wallet verification uses a read-only
          signature to confirm ownership. We never request, access, or store your private keys or
          seed phrase, and no BLOQ Talk employee or support agent will ever ask for them.
        </p>
      </Section>

      <Section heading="6. Access Controls & Least Privilege">
        <p>
          Access to production systems and personal data is restricted on a need-to-know basis
          using role-based access controls and the principle of least privilege. Privileged and
          destructive actions are recorded in a tamper-evident audit log for accountability.
        </p>
      </Section>

      <Section heading="7. BLOQ Shield Threat Screening">
        <p>
          BLOQ Shield screens links and messages for scams, phishing, malware, and fake support,
          and quarantines risky connection requests. Combined with the BLOQ Score and BLOQ Trust
          Gates, Shield reduces your exposure to malicious actors before they can reach you.
        </p>
      </Section>

      <Section heading="8. Infrastructure & Monitoring">
        <p>
          Our infrastructure runs in secured, access-controlled environments with network
          isolation, continuous monitoring, and automated alerting. We maintain regular backups
          and apply timely security patches to our systems and dependencies.
        </p>
      </Section>

      <Section heading="9. Vulnerability Management & Testing">
        <p>
          We continuously test the Service — including automated security self-checks and internal
          red-team exercises — to find and fix weaknesses before they can be exploited. We track
          and remediate vulnerabilities based on severity.
        </p>
      </Section>

      <Section heading="10. Incident Response">
        <p>
          We maintain an incident-response process to detect, contain, investigate, and remediate
          security events. If a breach affecting your personal data occurs, we will notify affected
          users and the appropriate authorities as required by applicable law.
        </p>
      </Section>

      <Section heading="11. Data Retention & Deletion">
        <p>
          We retain data only as long as needed to operate the Service or meet legal obligations.
          You can delete your account at any time; when you do, we delete or de-identify your
          personal data within a reasonable period. See our{" "}
          <a href="/privacy" className="text-bloq-mint hover:underline">Privacy Policy</a> for
          details on retention and your rights.
        </p>
      </Section>

      <Section heading="12. Responsible Disclosure">
        <p>
          No system is ever perfectly secure, and we welcome help from the security community. If
          you believe you've found a vulnerability, please report it responsibly to{" "}
          <a href="mailto:security@bloqtalk.app" className="text-bloq-mint hover:underline">
            security@bloqtalk.app
          </a>
          . We ask that you give us a reasonable opportunity to remediate before public disclosure.
        </p>
      </Section>

      <Section heading="13. Contact Us">
        <p>
          Questions about data security? Reach our security team at{" "}
          <a href="mailto:security@bloqtalk.app" className="text-bloq-mint hover:underline">
            security@bloqtalk.app
          </a>
          .
        </p>
      </Section>
    </LegalLayout>
  );
}
